import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Volume2, Sparkles, Delete, Bookmark, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { IconRenderer } from "../lib/icons";
import { speak } from "../lib/speech";
import Header from "../components/Header";
import UserNav from "../components/UserNav";

const CATEGORIES = [
  { id: "needs", label: "Needs", cls: "cat-needs" },
  { id: "feelings", label: "Feelings", cls: "cat-feelings" },
  { id: "food", label: "Food", cls: "cat-food" },
  { id: "activities", label: "Activities", cls: "cat-activities" },
  { id: "health", label: "Health", cls: "cat-health" },
  { id: "people", label: "People", cls: "cat-people" },
];

export default function CommBoard() {
  const navigate = useNavigate();
  const { canUsePro } = useAuth();
  const [board, setBoard] = useState(null);
  const [empty, setEmpty] = useState(false);
  const [cat, setCat] = useState("needs");
  const [said, setSaid] = useState("");
  const [thinking, setThinking] = useState(false);
  const [strip, setStrip] = useState([]);
  const [phrases, setPhrases] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const pid = localStorage.getItem("rb_profile");
        const { data } = await api.get("/boards", { params: pid ? { profile_id: pid } : {} });
        if (data.length === 0) setEmpty(true);
        else {
          setBoard(data[0]);
          try { setPhrases(JSON.parse(localStorage.getItem(`rb_phrases_${data[0].id}`) || "[]")); } catch { /* ignore */ }
        }
      } catch (e) {
        toast.error(apiError(e));
      }
    })();
  }, []);

  const tap = (tile) => {
    setStrip((s) => (s.some((x) => x.id === tile.id) || s.length >= 8 ? s : [...s, tile]));
    setSaid(tile.label);
    speak(tile.label);
  };

  const savePhrase = () => {
    if (strip.length === 0 || !board) return;
    if (phrases.length >= 20) {
      toast.error("You've saved 20 phrases — delete some first");
      return;
    }
    const next = [...phrases, { id: `${Date.now()}`, items: strip.map((t) => ({ id: t.id, label: t.label, icon: t.icon, image: t.image || null })) }];
    setPhrases(next);
    localStorage.setItem(`rb_phrases_${board.id}`, JSON.stringify(next));
    toast.success("Phrase saved — tap it any time to load it");
  };

  const removePhrase = (id) => {
    const next = phrases.filter((p) => p.id !== id);
    setPhrases(next);
    localStorage.setItem(`rb_phrases_${board.id}`, JSON.stringify(next));
  };

  const speakStrip = async () => {
    if (strip.length === 0) return;
    const text = strip.map((t) => t.label).join(" ");
    if (!canUsePro || strip.length === 1) {
      setSaid(text);
      speak(text);
      return;
    }
    const cacheKey = `rb_sent_${text.toLowerCase()}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      setSaid(cached);
      speak(cached);
      return;
    }
    setThinking(true);
    setSaid(text);
    speak(text);
    try {
      const { data } = await api.post("/ai/sentence", { label: text });
      localStorage.setItem(cacheKey, data.sentence);
      setSaid(data.sentence);
      speak(data.sentence);
    } catch { /* raw words already spoken */ } finally {
      setThinking(false);
    }
  };

  return (
    <div data-testid="comm-board" className="min-h-screen bg-background">
      <Header />
      <main className="max-w-4xl mx-auto px-4 py-6 pb-32">
        <button data-testid="back-button" onClick={() => navigate("/use")} className="min-h-[48px] px-4 rounded-full border-2 border-foreground/25 bg-white font-bold flex items-center gap-2 hover:bg-secondary transition-colors duration-200">
          <ArrowLeft size={20} strokeWidth={2.5} /> Back
        </button>

        {empty ? (
          <p data-testid="no-board-message" className="mt-8 bg-white rounded-3xl border-2 border-dashed border-foreground/20 p-8 text-lg text-muted-foreground">
            No talk board yet. Ask your carer to create one in Carer Mode.
          </p>
        ) : !board ? (
          <p className="mt-8 text-lg text-muted-foreground">Loading…</p>
        ) : (
          <>
            <div
              data-testid="spoken-display"
              className="mt-5 min-h-[72px] bg-white rounded-3xl border-2 border-primary/40 px-5 flex items-center gap-3"
            >
              <Volume2 size={32} strokeWidth={2.5} className="text-primary shrink-0" />
              <p className="text-2xl font-extrabold truncate">{said || "Tap a picture to speak"}</p>
              {canUsePro && (
                <span data-testid="smart-sentence-badge" className={`ml-auto shrink-0 flex items-center gap-1 text-sm font-bold text-[#9F7AEA] ${thinking ? "animate-pulse" : ""}`}>
                  <Sparkles size={18} strokeWidth={2.5} /> {thinking ? "Thinking…" : "Smart sentence"}
                </span>
              )}
            </div>

            <div data-testid="sentence-strip" aria-live="polite" aria-label="Sentence strip" className="mt-4 bg-white rounded-3xl border-2 border-foreground/15 p-4">
              <div className="flex flex-wrap items-center gap-2 min-h-[64px]">
                {strip.length === 0 && <p className="text-muted-foreground font-semibold px-2">Tap pictures to build a sentence…</p>}
                {strip.map((t) => (
                  <button
                    key={t.id}
                    data-testid={`strip-tile-${t.id}`}
                    onClick={() => setStrip((s) => s.filter((x) => x.id !== t.id))}
                    aria-label={`Remove ${t.label} from sentence`}
                    className="min-h-[64px] px-4 rounded-2xl border-2 border-foreground/15 bg-secondary/60 font-extrabold text-lg flex items-center gap-2 hover:bg-secondary transition-colors duration-200"
                  >
                    <IconRenderer name={t.icon} size={26} />
                    {t.label}
                  </button>
                ))}
              </div>
              <div className="mt-3 flex gap-2">
                <button
                  data-testid="strip-speak-button"
                  onClick={speakStrip}
                  disabled={strip.length === 0}
                  className="flex-1 min-h-[64px] rounded-full bg-primary text-white text-xl font-bold flex items-center justify-center gap-2 transition-transform duration-200 ease-out hover:-translate-y-px disabled:opacity-50"
                >
                  <Volume2 size={26} strokeWidth={2.5} /> Speak
                </button>
                <button
                  data-testid="strip-backspace-button"
                  onClick={() => setStrip((s) => s.slice(0, -1))}
                  disabled={strip.length === 0}
                  aria-label="Remove last picture"
                  className="min-w-[64px] min-h-[64px] rounded-full border-2 border-foreground/25 bg-white grid place-items-center hover:bg-secondary transition-colors duration-200 disabled:opacity-50"
                >
                  <Delete size={24} strokeWidth={2.5} />
                </button>
                <button
                  data-testid="strip-save-button"
                  onClick={savePhrase}
                  disabled={strip.length === 0}
                  aria-label="Save as quick phrase"
                  className="min-w-[64px] min-h-[64px] rounded-full border-2 border-foreground/25 bg-white grid place-items-center hover:bg-secondary transition-colors duration-200 disabled:opacity-50"
                >
                  <Bookmark size={24} strokeWidth={2.5} />
                </button>
                <button
                  data-testid="strip-clear-button"
                  onClick={() => setStrip([])}
                  disabled={strip.length === 0}
                  className="min-h-[64px] px-6 rounded-full border-2 border-foreground/25 bg-white text-lg font-bold hover:bg-secondary transition-colors duration-200 disabled:opacity-50"
                >
                  Clear
                </button>
              </div>
            </div>

            {phrases.length > 0 && (
              <div data-testid="quick-phrases" className="mt-4 flex flex-wrap gap-2">
                {phrases.map((p) => (
                  <span key={p.id} className="flex items-center gap-1 rounded-full border-2 border-primary/30 bg-primary/5 pl-2 pr-1 py-1">
                    <button
                      data-testid={`phrase-${p.id}`}
                      onClick={() => setStrip(p.items)}
                      className="min-h-[48px] px-3 font-bold text-base flex items-center gap-2"
                    >
                      {p.items.map((t) => t.label).join(" ")}
                    </button>
                    <button
                      data-testid={`phrase-remove-${p.id}`}
                      onClick={() => removePhrase(p.id)}
                      aria-label="Delete saved phrase"
                      className="min-w-[40px] min-h-[40px] rounded-full grid place-items-center hover:bg-secondary"
                    >
                      <X size={16} strokeWidth={2.5} />
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div className="mt-5 grid grid-cols-3 sm:grid-cols-6 gap-2">
              {CATEGORIES.map((c) => (
                <button
                  key={c.id}
                  data-testid={`category-tab-${c.id}`}
                  onClick={() => setCat(c.id)}
                  className={`min-h-[56px] rounded-2xl border-2 font-bold text-sm sm:text-base transition-colors duration-200 ${
                    cat === c.id ? "border-primary bg-primary/10" : "border-foreground/20 bg-white hover:bg-secondary"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>

            <div data-testid="board-grid" className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {board.tiles.filter((t) => t.category === cat).map((t) => {
                const c = CATEGORIES.find((x) => x.id === t.category) || CATEGORIES[0];
                return (
                  <button
                    key={t.id}
                    data-testid={`tile-${t.id}`}
                    onClick={() => tap(t)}
                    aria-label={`Speak: ${t.label}`}
                    className={`bg-white rounded-3xl border-2 ${c.cls} p-4 aspect-square flex flex-col items-center justify-center gap-2 transition-transform duration-200 ease-out hover:-translate-y-0.5 active:scale-95`}
                  >
                    {t.image ? (
                      <img src={t.image} alt="" className="w-20 h-20 rounded-2xl object-cover" />
                    ) : (
                      <IconRenderer name={t.icon} size={56} />
                    )}
                    <span className="text-xl font-extrabold leading-tight text-center">{t.label}</span>
                  </button>
                );
              })}
              {board.tiles.filter((t) => t.category === cat).length === 0 && (
                <p className="col-span-full text-lg text-muted-foreground p-6 text-center">No pictures here yet.</p>
              )}
            </div>
          </>
        )}
      </main>
      <UserNav />
    </div>
  );
}
